import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Async } from 'commons/js/components/Select'

import { error } from 'commons/js/util/logger'
import { Button, Icon } from '@elparking/components'
import { defineMessages, injectIntl } from 'react-intl'

const MIN_LENGTH_FOR_SEARCH = 3

const optionsForSelect = {
    multi: false,
    cache: false,
    searchPromptText: '',
    loadingPlaceholder: '',
    autosize: false,
    onBlurResetsInput: false,
    onCloseResetsInput: false,
    onSelectResetsInput: false,
    filterOptions: false,
}

const messages = defineMessages({
    ariaOptionsMessage: 'El foco está actualmente en la opción {label}{disabledMessage}',
    ariaSelectedMessage: 'La opción seleccionada es {label}',
    ariaGuidanceMessage: 'Empiece a escribir para buscar opciones. Navegue por las opciones con las teclas de flecha arriba y abajo. Presione la tecla Enter para seleccionar una opción.',
})
const PlacesSelect = ({
    className,
    value: propValue,
    initialSearchValue,
    hideClearButton = true,
    autofocus = false,
    placeholder = 'Selecciona un lugar...',
    autocompleteService = () => Promise.resolve([]),
    optionRenderer,
    onChange = () => {},
    dataTest,
    customStyles,
    customTheme,
    error: componentError,
    errorComponentId = 'form-error-field',
    ariaRoleDescription,
    intl,
}) => {
    const [value, setValue] = useState(propValue)
    const [results, setResults] = useState([])
    const [input, setInput] = useState(null)
    const [literalInput, setLiteralInput] = useState(null)

    useEffect(() => {
        if (propValue) {
            const valueHasChanged = !value || propValue.value !== value.value
            if (valueHasChanged) {
                const label = propValue.label || value?.label
                setValue({ value: propValue, label })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propValue])

    useEffect(() => {
        if (initialSearchValue?.label) {
            const valueToSearch = initialSearchValue.inputSearch || initialSearchValue.label
            autocompleteService({ input: valueToSearch })
                .then((results) => {
                    setResults(results)
                    setLiteralInput(valueToSearch)
                    setValue(initialSearchValue)
                    onChange(initialSearchValue)
                })
                .catch((e) => error(e.message))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSetValue = useCallback(
        ({ value, label }, next = () => {}) => {
            const nextVal = { value, label }
            setValue(nextVal)
            next(nextVal, literalInput)
        },
        [literalInput]
    )

    const handleChange = useCallback(
        (option) => {
            if (!option || !option.value) return
            setInput(option.label)
            handleSetValue(option, onChange)
        },
        [handleSetValue, onChange]
    )

    const loadOptions = useCallback(
        (input) => {
            setInput(input)
            setLiteralInput(input)
            const length = input.length
            if (length === 0) {
                return Promise.resolve(results)
            }
            if (length < MIN_LENGTH_FOR_SEARCH) {
                return Promise.resolve([])
            }
            return autocompleteService({ input })
                ?.then((results) => {
                    setResults(results)
                    return Promise.resolve(results)
                })
                ?.catch((e) => error(e.message))
        },
        [autocompleteService, results]
    )

    const getAriaLiveMessageOnFocus = useCallback(({ focused, isDisabled }) => {
        return intl.formatMessage(messages.ariaOptionsMessage, {label: focused.label, disabledMessage: isDisabled ? ', que está desactivada.' : ''})
    }, [intl])

    const renderClearButton = useCallback(() => {
        if (hideClearButton) return null
        return (
            <Button.unstyled
              type='button'
              onClick={() => {
                    setInput('')
                    handleSetValue({ value: '', label: '' }, onChange)
                }}
              fontColor='inherit'
            >
                <Icon type='x' fontColor='inherit' />
            </Button.unstyled>
        )
    }, [hideClearButton, handleSetValue, onChange])

    return (
        <Async
          dataTest={dataTest}
          className={className}
          value={value}
          loadOptions={loadOptions}
          autoFocus={autofocus}
          autoBlur
          defaultOptions={results}
          placeholder={placeholder}
          clearComponent={renderClearButton}
          onChange={handleChange}
          optionRenderer={optionRenderer}
          valueRenderer={() => (input === null ? value?.label : input)}
          {...optionsForSelect}
          styles={customStyles ?? {}}
          theme={customTheme ?? {}}
          ariaLiveMessages={{
              onFocus: getAriaLiveMessageOnFocus,
              guidance: () => intl.formatMessage(messages.ariaGuidanceMessage),
              onFilter: () => '',
              onChange: () => intl.formatMessage(messages.ariaSelectedMessage, { label: value?.label }),
          }}
          aria-invalid={!!componentError}
          aria-errormessage={errorComponentId}
          aria-label={ariaRoleDescription}
        />
    )
}

PlacesSelect.propTypes = {
    className: PropTypes.string,
    value: PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.any,
    }),
    initialSearchValue: PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.any,
        inputSearch: PropTypes.string,
    }),
    hideClearButton: PropTypes.bool,
    autofocus: PropTypes.bool,
    placeholder: PropTypes.string,
    autocompleteService: PropTypes.func,
    optionRenderer: PropTypes.func,
    onChange: PropTypes.func,
    dataTest: PropTypes.string,
    customStyles: PropTypes.object,
    customTheme: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    error: PropTypes.bool,
    errorComponentId: PropTypes.string,
    ariaRoleDescription: PropTypes.string,
    intl: PropTypes.object,
}

export default injectIntl(PlacesSelect)
